<template>
    <div class="w-full md:w-fit">
        <Button
            @click="isDrawerOpen = true"
            :disabled="reservation.cannotReschedule"
            iconName="arrow-rotate"
            color="light"
            size="small"
            class="w-full"
        >
            Reschedule
        </Button>
    </div>

    <Drawer
        @close="isDrawerOpen = false"
        @onDrawerOpened="fetchAvailableDates()"
        :show="isDrawerOpen"
        width="wide"
    >
        <template #title>Reschedule Meeting</template>
        <template #content>
            <div class="space-y-2">
                <SchedulingErrorMessage
                    v-if="errorMessage"
                    :errorMessage="errorMessage"
                />
                <div
                    class="grid grid-cols-12 space-x-0 lg:space-x-2 space-y-4 lg:space-y-0"
                >
                    <div class="col-span-12 lg:col-span-7 space-y-2">
                        <TimezoneSelector
                            :value="selectedTimeZone ?? undefined"
                            :useBookingFormThemeCssVariables="true"
                            :required="true"
                            @onSelectionChange="
                                handleTimeZoneSelectionChange($event.value)
                            "
                        />

                        <AvailabilityCalendar
                            :months="availabilityResult.months"
                            :selectedDate="
                                selectedAvailabilityResultDate?.dateInSelectedTimeZoneForDisplay ??
                                undefined
                            "
                            :isLoading="isLoading"
                            :useBookingFormThemeCssVariables="true"
                            :localizer="localizer"
                            @onDateSelectionChange="
                                handleDateSelectionChange($event.date)
                            "
                            @onMonthSelectionChange="
                                handleMonthSelectionChanged()
                            "
                        />
                    </div>
                    <template v-if="selectedAvailabilityResultDate">
                        <div
                            class="col-span-12 lg:col-span-5 flex flex-col gap-2"
                        >
                            <TimeslotFormatSelector
                                :currentTimeslotTimeFormat="
                                    selectedTimeslotFormat
                                "
                                @onChange="
                                    handleTimeslotFormatChange($event.format)
                                "
                            />

                            <TimeSlotList
                                :availabilityResultDate="
                                    selectedAvailabilityResultDate
                                "
                                :shouldRenderAvailableSeats="false"
                                :selectedAvailabilityResultTimeSlot="
                                    selectedAvailabilityResultTimeSlot
                                "
                                :useBookingFormThemeCssVariables="true"
                                @onTimeSlotSelectionChange="
                                    handleTimeSlotSelectionChanged(
                                        $event.availabilityResultTimeSlot,
                                    )
                                "
                                :timeslotTimeFormat="selectedTimeslotFormat"
                            />
                        </div>
                    </template>
                </div>

                <ApiForm
                    :url="
                        urlFactory.makeMeetingAttendanceRescheduleUrl(
                            attendance.meetingId,
                            attendance.id,
                        )
                    "
                    method="POST"
                    :disableCatch="false"
                    ref="rescheduleMeetingApiFormRef"
                >
                    <input
                        type="hidden"
                        :name="RequestBodyKeyEnum.RescheduleMeeting_TimeZone"
                        :value="selectedTimeZone"
                    />
                    <input
                        type="hidden"
                        :name="
                            RequestBodyKeyEnum.RescheduleMeeting_DateForScheduling
                        "
                        :value="
                            selectedAvailabilityResultDate?.dateInAvailabilityTimeZoneForScheduling
                        "
                    />
                    <input
                        type="hidden"
                        :name="
                            RequestBodyKeyEnum.RescheduleMeeting_TimeSlotStartForScheduling
                        "
                        :value="
                            selectedAvailabilityResultTimeSlot
                                ?.timeSlotInAvailabilityTimeZoneForScheduling
                                .start
                        "
                    />
                    <input
                        type="hidden"
                        :name="
                            RequestBodyKeyEnum.RescheduleMeeting_TimeSlotEndForScheduling
                        "
                        :value="
                            selectedAvailabilityResultTimeSlot
                                ?.timeSlotInAvailabilityTimeZoneForScheduling
                                .end
                        "
                    />
                </ApiForm>
            </div>
        </template>
        <template #actions>
            <Button
                @click="isDrawerOpen = false"
                color="light"
            >
                Cancel
            </Button>
            <Button
                @click="submitRescheduleMeetingForm()"
                :disabled="
                    !selectedAvailabilityResultTimeSlot ||
                    !selectedAvailabilityResultDate ||
                    !selectedTimeZone
                "
                iconName="arrow-rotate"
                :loading="isLoading.valueOf()"
                :useBookingFormThemeCssVariables="true"
            >
                Reschedule
            </Button>
        </template>
    </Drawer>
</template>

<!-- TODO: Clean up state after API request is successful -->
<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import { ref, type PropType } from 'vue'
    import { type ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import Drawer from '@/Shared/Component/Drawer.vue'
    import Button from '@/Shared/Component/Button.vue'
    import AvailabilityCalendar from '@/BookingForm/BookingForm/Component/AvailabilityCalendar.vue'
    import SchedulingErrorMessage from '@/BookingForm/BookingForm/Component/SchedulingErrorMessage.vue'
    import TimeSlotList from '@/BookingForm/BookingForm/Component/TimeSlotList.vue'
    import useApiRequest from '@/Shared/Hook/useApiRequest'
    import MeetingData from '@/App/Booking/Meeting/Entity/MeetingData'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import {
        ResponseJsonKeyEnum,
        type AvailabilityResultDateType,
        type AvailabilityResultTimeSlotType,
    } from '@/Shared/AutoGenerated/Main'
    import ApiFormResponseType from '@/Shared/Component/Forms/ApiFormResponseType'
    import ApiResponseType from '@/Shared/Hook/ApiResponseType'
    import Meeting from '@/App/Booking/Meeting/Entity/Meeting'
    import Attendance from '@/App/Booking/Attendance/Entity/Attendance'
    import Reservation from '@/App/Booking/Reservation/Entity/Reservation'
    import AvailabilityResult from '@/App/Availability/Entity/AvailabilityResult'
    import TimezoneSelector from '@/BookingForm/BookingForm/Component/TimezoneSelector.vue'
    import LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import { LocaleEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
    import TimeslotFormatSelector from '@/BookingForm/BookingForm/Component/TimeslotFormatSelector.vue'
    import { TimeslotTimeFormat } from '@/Shared/Hook/useDate'

    const emits = defineEmits<{
        onSuccess: [{ meetingData: MeetingData }]
    }>()

    const props = defineProps({
        meeting: {
            type: Object as PropType<Meeting>,
            required: true,
        },
        attendance: {
            type: Object as PropType<Attendance>,
            required: true,
        },
        reservation: {
            type: Object as PropType<Reservation>,
            required: true,
        },
    })

    const localizer = LocalizerService.make(LocaleEnum.English)

    // -------------------------
    // State
    // -------------------------

    const isDrawerOpen = ref(false)

    const isLoading = ref<boolean>(false)
    const errorMessage = ref<string | null>(null)

    const availabilityResult = ref<AvailabilityResult>(
        AvailabilityResult.newEmpty(),
    )

    const selectedAvailabilityResultDate =
        ref<AvailabilityResultDateType | null>(null)
    const selectedAvailabilityResultTimeSlot = ref<
        AvailabilityResultTimeSlotType | undefined
    >(undefined)
    const selectedTimeZone = ref<string | null>(
        props.reservation.selectedTimeZone,
    )

    // -------------------------
    // Event handlers
    // -------------------------

    const handleTimeZoneSelectionChange = (timeZone: string | null) => {
        if (!timeZone) {
            throw new Error('Timezone should be a string', { cause: timeZone })
        }

        // Prevent a new request if the timezone didn't change
        if (timeZone === selectedTimeZone.value) {
            return
        }

        selectedAvailabilityResultDate.value = null
        selectedAvailabilityResultTimeSlot.value = undefined
        selectedTimeZone.value = timeZone

        fetchAvailableDates()
    }

    const handleDateSelectionChange = (value: string | null) => {
        if (!value) {
            selectedAvailabilityResultDate.value = null

            return
        }

        // Prevent a new request if the date didn't change
        if (
            value ===
            selectedAvailabilityResultDate.value
                ?.dateInSelectedTimeZoneForDisplay
        ) {
            return
        }

        selectedAvailabilityResultTimeSlot.value = undefined
        selectedAvailabilityResultDate.value =
            availabilityResult.value.getAvailabilityResultDate(value)
    }

    const handleTimeSlotSelectionChanged = (
        availabilityResultTimeSlot: AvailabilityResultTimeSlotType | null,
    ) => {
        if (!availabilityResultTimeSlot) {
            return
        }

        selectedAvailabilityResultTimeSlot.value = availabilityResultTimeSlot
    }

    const handleMonthSelectionChanged = () => {
        selectedAvailabilityResultDate.value = null
        selectedAvailabilityResultTimeSlot.value = undefined
    }

    // -------------------------
    // API calls
    // -------------------------

    const fetchAvailableDates = () => {
        if (!selectedTimeZone.value) {
            return
        }

        isLoading.value = true

        selectedAvailabilityResultDate.value = null
        selectedAvailabilityResultTimeSlot.value = undefined

        useApiRequest()
            .get(
                urlFactory.makeMeetingAvailableDatesUrl(
                    props.meeting.id,
                    selectedTimeZone.value,
                ),
            )
            .then((response: ApiResponseType) => {
                errorMessage.value = null

                const calendarMonths = response.getHydrateCalendarMonths(
                    ResponseJsonKeyEnum.Rescheduling_IndexAvailableDates_CalendarMonths,
                )
                const availabilityResultDates =
                    response.getHydratedAvailabilityResultDates(
                        ResponseJsonKeyEnum.Rescheduling_IndexAvailableDates_AvailabilityResultDates,
                    )

                availabilityResult.value = new AvailabilityResult(
                    calendarMonths,
                    availabilityResultDates,
                )
            })
            .catch((error) => {
                // TODO: Emit event with error message???
                errorMessage.value =
                    'Error when trying to reschedule meeting, please talk to us.'

                console.error(error)
            })
            .finally(() => {
                isLoading.value = false
            })
    }

    const rescheduleMeetingApiFormRef = ref<ApiFormRefType>(null)

    const submitRescheduleMeetingForm = () => {
        errorMessage.value = null

        if (!selectedTimeZone.value) {
            return
        }

        if (!selectedAvailabilityResultDate.value) {
            return
        }

        if (!selectedAvailabilityResultTimeSlot.value) {
            return
        }

        isLoading.value = true

        rescheduleMeetingApiFormRef.value
            ?.submit()
            ?.then((response: ApiFormResponseType) => {
                errorMessage.value = null
                isDrawerOpen.value = false

                emits('onSuccess', {
                    meetingData: response.getHydratedMeetingData(
                        ResponseJsonKeyEnum.Rescheduling_MeetingDataData,
                    ),
                })
            })
            ?.catch((error) => {
                errorMessage.value =
                    'Error when trying to reschedule meeting, please talk to us.'

                console.error(error)
            })
            ?.finally(() => {
                isLoading.value = false
            })
    }

    // -------------------------
    // Timeslot format selection
    // -------------------------

    const selectedTimeslotFormat = ref<TimeslotTimeFormat>(
        TimeslotTimeFormat.Format24Hours,
    )

    const handleTimeslotFormatChange = (value: TimeslotTimeFormat) => {
        selectedTimeslotFormat.value = value
    }
    const urlFactory = new UrlFactory()
</script>
